import { useFieldArray, UseFormReturn } from 'react-hook-form'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { Label } from '@/common/ui/label'
import { Switch } from '@/common/ui/switch'
import { Checkbox } from '@/common/ui/checkbox'
import { Badge } from '@/common/ui/badge'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Plus, Trash2, Shuffle, Target } from 'lucide-react'
import useQueryGetGameProviderList from '@/modules/game/api/hooks/useQueryGetGameProviderList'
import useQueryGetGameList from '@/modules/game/api/hooks/useQueryGetGameList'
import useQueryGetPresetId from '@/modules/promotion/api/hooks/useQueryGetPresetId'
import { FilterSelector } from '../FilterSelector'
import { GameType } from '../../types'
import { useState, useEffect, useMemo } from 'react'
import { RtpPresetPrecedenceTooltip } from '@/common/components/RtpPresetPrecedenceTooltip'

const GAME_TYPES = [
	GameType.SLOT,
	GameType.CASINO,
	GameType.FISHING,
	GameType.SPORT,
	GameType.LOTTO,
] as const

interface FlexHotGamesFormProps {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	form: UseFormReturn<any>
	disabled?: boolean
}

export const FlexHotGamesForm = ({
	form,
	disabled = false,
}: FlexHotGamesFormProps) => {
	const { fields, append, remove } = useFieldArray({
		control: form.control,
		name: 'games',
	})

	// Fetch all providers
	const { data: providersData } = useQueryGetGameProviderList({
		skip: 0,
		take: 1000,
	})

	// Fetch all games for filter selector
	const { data: allGamesData } = useQueryGetGameList({
		skip: 0,
		take: 10000,
	})

	// Fetch presets
	const { data: presetsData } = useQueryGetPresetId()

	const addGame = () => {
		if (fields.length < 10) {
			append({
				isRandomized: false,
				providerId: '',
				gameId: '',
				gameType: GameType.SLOT,
				gameTypes: [GameType.SLOT],
				preset: '',
				slotData: {
					winProbability: 0,
					freeSpinChance: 0,
					maxMultiplier: 1,
				},
				nonSlotData: {
					bonusAmount: 0,
				},
				providerFilter: {
					includedProviderIds: [],
					excludedProviderIds: [],
				},
				gameFilter: {
					includedGameIds: [],
					excludedGameIds: [],
				},
				presetFilter: {
					includedPresets: [],
					excludedPresets: [],
				},
				slotDataRange: {
					winProbabilityMin: 0,
					winProbabilityMax: 100,
					freeSpinChanceMin: 0,
					freeSpinChanceMax: 100,
					maxMultiplierMin: 1,
					maxMultiplierMax: 10000,
				},
				nonSlotDataRange: {
					bonusAmountMin: 0,
					bonusAmountMax: 1000,
				},
			})
		}
	}

	const removeGame = (index: number) => {
		if (fields.length > 1) {
			remove(index)
		}
	}

	return (
		<div className="space-y-6">
			{/* Name and Description */}
			<Card>
				<CardHeader>
					<CardTitle>ข้อมูล Flex Message</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<FormField
						control={form.control}
						name="name"
						render={({ field }) => (
							<FormItem>
								<FormLabel>ชื่อ Flex Message</FormLabel>
								<FormControl>
									<Input
										{...field}
										placeholder="เช่น Hot Games สำหรับสมาชิกใหม่"
										disabled={disabled}
									/>
								</FormControl>
								<FormDescription>
									ชื่อสำหรับอ้างอิงภายในระบบ (ผู้เล่นไม่เห็น)
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="description"
						render={({ field }) => (
							<FormItem>
								<FormLabel>คำอธิบาย (ถ้ามี)</FormLabel>
								<FormControl>
									<Textarea
										{...field}
										placeholder="อธิบายรายละเอียดเพิ่มเติม..."
										className="min-h-[80px]"
										disabled={disabled}
									/>
								</FormControl>
								<FormDescription>
									คำอธิบายเพิ่มเติมสำหรับแอดมิน (ไม่บังคับ)
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>
				</CardContent>
			</Card>

			{/* Games Configuration */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center justify-between">
						<span>ตั้งค่าเกม</span>
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={addGame}
							disabled={disabled || fields.length >= 10}
						>
							<Plus className="mr-2 h-4 w-4" />
							เพิ่มเกม
						</Button>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-6">
					{fields.length === 0 && (
						<div className="text-center text-muted-foreground">
							<p>ยังไม่มีเกม กรุณาเพิ่มเกมอย่างน้อย 1 เกม</p>
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={addGame}
								disabled={disabled}
								className="mt-4"
							>
								<Plus className="mr-2 h-4 w-4" />
								เพิ่มเกม
							</Button>
						</div>
					)}

					{fields.map((field, index) => (
						<GameItem
							key={field.id}
							form={form}
							index={index}
							providers={providersData?.data || []}
							allGames={allGamesData?.data || []}
							presets={presetsData?.presets || []}
							onRemove={() => removeGame(index)}
							canRemove={fields.length > 1}
							disabled={disabled}
						/>
					))}

					{fields.length > 0 && (
						<>
							<div className="flex items-center justify-between">
								<div className="text-sm text-muted-foreground">
									เกมที่เพิ่มแล้ว: {fields.length} / 10
								</div>
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={addGame}
									disabled={disabled || fields.length >= 10}
								>
									<Plus className="mr-2 h-4 w-4" />
									เพิ่มเกม
								</Button>
							</div>
						</>
					)}
				</CardContent>
			</Card>
		</div>
	)
}

interface GameItemProps {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	form: UseFormReturn<any>
	index: number
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	providers: any[]
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	allGames: any[]
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	presets: any[]
	onRemove: () => void
	canRemove: boolean
	disabled?: boolean
}

const GameItem = ({
	form,
	index,
	providers,
	allGames,
	presets,
	onRemove,
	canRemove,
	disabled = false,
}: GameItemProps) => {
	const isRandomized = form.watch(`games.${index}.isRandomized`)
	const selectedProviderId = form.watch(`games.${index}.providerId`)
	const selectedGameId = form.watch(`games.${index}.gameId`)
	const selectedGameTypes: GameType[] =
		form.watch(`games.${index}.gameTypes`) || []
	const formGameType: GameType =
		form.watch(`games.${index}.gameType`) || GameType.SLOT
	const [selectedGameType, setSelectedGameType] =
		useState<GameType>(formGameType)

	// Sync selectedGameType when the form value changes externally (e.g. on reset)
	useEffect(() => {
		if (formGameType && formGameType !== selectedGameType) {
			setSelectedGameType(formGameType)
		}
		// Only run when formGameType changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formGameType])

	// Filter fields for randomized mode
	const providerFilter = form.watch(`games.${index}.providerFilter`) || {
		includedProviderIds: [],
		excludedProviderIds: [],
	}
	const gameFilter = form.watch(`games.${index}.gameFilter`) || {
		includedGameIds: [],
		excludedGameIds: [],
	}
	const presetFilter = form.watch(`games.${index}.presetFilter`) || {
		includedPresets: [],
		excludedPresets: [],
	}

	// Fetch games for selected provider (fixed mode)
	const { data: gamesData } = useQueryGetGameList({
		skip: 0,
		take: 1000,
		providerName: providers.find((p) => p.id === selectedProviderId)?.name,
	})

	// Convert games to filter items format
	// Look up provider by providerId to get unique provider name (not displayName which can be duplicated)
	const gameFilterItems = useMemo(() => {
		return allGames.map((game) => {
			const gameName = game.displayName || game.name
			const provider = providers.find((p) => p.id === game.providerId)
			const providerLabel =
				provider?.name || provider?.displayName || game.providerName
			const displayName = providerLabel
				? `${gameName} (${providerLabel})`
				: gameName
			return {
				id: game.id,
				name: game.name,
				displayName,
			}
		})
	}, [allGames, providers])

	// Convert providers to filter items format
	const providerFilterItems = useMemo(() => {
		return providers.map((provider) => ({
			id: provider.id,
			name: provider.name,
			displayName: provider.name || provider.displayName,
		}))
	}, [providers])

	// Convert presets to filter items format
	const presetFilterItems = useMemo(() => {
		return presets.map((preset) => ({
			id: preset.id,
			name: preset.note || preset.id,
			displayName: `${preset.note} (RTP: ${preset.rtpNormal}%)`,
		}))
	}, [presets])

	// Reset gameId when provider changes (fixed mode)
	useEffect(() => {
		if (!isRandomized && selectedProviderId) {
			if (selectedGameId && gamesData?.data) {
				const gameExists = gamesData.data.find((g) => g.id === selectedGameId)
				if (!gameExists) {
					form.setValue(`games.${index}.gameId`, '')
				}
			}
		}
	}, [selectedProviderId, gamesData, selectedGameId, form, index, isRandomized])

	// Auto-detect game type when game is selected (fixed mode)
	useEffect(() => {
		if (!isRandomized && selectedGameId && gamesData?.data) {
			const selectedGame = gamesData.data.find((g) => g.id === selectedGameId)
			if (selectedGame) {
				const gameType = selectedGame.type as GameType
				setSelectedGameType(gameType)
				form.setValue(`games.${index}.gameType`, gameType)
			}
		}
	}, [selectedGameId, gamesData, form, index, isRandomized])

	// Check if slot or non-slot fields should be shown (randomized mode)
	const hasSlotType = selectedGameTypes.includes(GameType.SLOT)
	const hasNonSlotType = selectedGameTypes.some((t) => t !== GameType.SLOT)

	// For fixed mode
	const isSlot = selectedGameType === GameType.SLOT

	const handleGameTypeToggle = (type: GameType) => {
		const currentTypes = selectedGameTypes || []
		const isSelected = currentTypes.includes(type)
		const newTypes = isSelected
			? currentTypes.filter((t) => t !== type)
			: [...currentTypes, type]

		form.setValue(`games.${index}.gameTypes`, newTypes)
	}

	return (
		<div className="space-y-4 rounded-lg border p-4">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3">
					<h4 className="font-medium">เกมที่ {index + 1}</h4>
					{isRandomized ? (
						<Badge variant="secondary" className="gap-1">
							<Shuffle className="h-3 w-3" />
							สุ่ม
						</Badge>
					) : (
						<Badge variant="outline" className="gap-1">
							<Target className="h-3 w-3" />
							กำหนดเอง
						</Badge>
					)}
				</div>
				{canRemove && (
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={onRemove}
						disabled={disabled}
					>
						<Trash2 className="h-4 w-4 text-destructive" />
					</Button>
				)}
			</div>

			{/* Mode Toggle */}
			<FormField
				control={form.control}
				name={`games.${index}.isRandomized`}
				render={({ field }) => (
					<FormItem className="flex items-center justify-between rounded-md border p-3">
						<div className="space-y-0.5">
							<FormLabel>โหมดสุ่มเกม</FormLabel>
							<FormDescription className="text-xs">
								{field.value
									? 'ระบบจะสุ่มเลือกเกมตามเงื่อนไขที่กำหนด'
									: 'เลือกเกมที่ต้องการโดยตรง'}
							</FormDescription>
						</div>
						<FormControl>
							<Switch
								checked={field.value}
								onCheckedChange={field.onChange}
								disabled={disabled}
							/>
						</FormControl>
					</FormItem>
				)}
			/>

			{isRandomized ? (
				<RandomizedModeFields
					form={form}
					index={index}
					disabled={disabled}
					selectedGameTypes={selectedGameTypes}
					hasSlotType={hasSlotType}
					hasNonSlotType={hasNonSlotType}
					onGameTypeToggle={handleGameTypeToggle}
					providerFilterItems={providerFilterItems}
					gameFilterItems={gameFilterItems}
					presetFilterItems={presetFilterItems}
					providerFilter={providerFilter}
					gameFilter={gameFilter}
					presetFilter={presetFilter}
				/>
			) : (
				<FixedModeFields
					form={form}
					index={index}
					disabled={disabled}
					providers={providers}
					gamesData={gamesData}
					presets={presets}
					selectedProviderId={selectedProviderId}
					isSlot={isSlot}
				/>
			)}
		</div>
	)
}

interface FixedModeFieldsProps {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	form: UseFormReturn<any>
	index: number
	disabled: boolean
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	providers: any[]
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	gamesData: any
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	presets: any[]
	selectedProviderId: string
	isSlot: boolean
}

const FixedModeFields = ({
	form,
	index,
	disabled,
	providers,
	gamesData,
	presets,
	selectedProviderId,
	isSlot,
}: FixedModeFieldsProps) => {
	return (
		<>
			{/* Provider selector */}
			<FormField
				control={form.control}
				name={`games.${index}.providerId`}
				render={({ field }) => (
					<FormItem>
						<FormLabel>ผู้ให้บริการเกม</FormLabel>
						<Select
							onValueChange={(value) => {
								field.onChange(value)
								form.setValue(`games.${index}.gameId`, '')
							}}
							value={field.value}
							disabled={disabled}
						>
							<FormControl>
								<SelectTrigger>
									<SelectValue placeholder="เลือกผู้ให้บริการ" />
								</SelectTrigger>
							</FormControl>
							<SelectContent>
								{providers.map((provider) => (
									<SelectItem key={provider.id} value={provider.id}>
										{provider.name || provider.displayName}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<FormMessage />
					</FormItem>
				)}
			/>

			{/* Game selector */}
			<FormField
				control={form.control}
				name={`games.${index}.gameId`}
				render={({ field }) => (
					<FormItem>
						<FormLabel>เกม</FormLabel>
						<Select
							onValueChange={field.onChange}
							value={field.value}
							disabled={disabled || !selectedProviderId}
						>
							<FormControl>
								<SelectTrigger>
									<SelectValue placeholder="เลือกเกม" />
								</SelectTrigger>
							</FormControl>
							<SelectContent>
								{gamesData?.data?.map((game: { id: string; name: string }) => (
									<SelectItem key={game.id} value={game.id}>
										{game.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<FormDescription>
							{selectedProviderId
								? 'เลือกเกมจากผู้ให้บริการที่เลือก'
								: 'กรุณาเลือกผู้ให้บริการก่อน'}
						</FormDescription>
						<FormMessage />
					</FormItem>
				)}
			/>

			{/* Preset Selection (optional) */}
			<FormField
				control={form.control}
				name={`games.${index}.preset`}
				render={({ field }) => (
					<FormItem>
						<div className="flex items-center gap-2">
							<FormLabel>RTP Preset (ถ้ามี)</FormLabel>
							<RtpPresetPrecedenceTooltip highlightLevel={2} />
						</div>
						<Select
							onValueChange={(value) => {
								field.onChange(value === 'none' ? undefined : value)
							}}
							value={field.value || 'none'}
							disabled={disabled}
						>
							<FormControl>
								<SelectTrigger>
									<SelectValue placeholder="เลือก RTP Preset" />
								</SelectTrigger>
							</FormControl>
							<SelectContent>
								<SelectItem value="none">ไม่ใช้ RTP Preset</SelectItem>
								{presets.map(
									(preset: {
										id: string
										note: string
										rtpNormal: number
										rtpBuyFeature: number
									}) => (
										<SelectItem key={preset.id} value={preset.id}>
											{preset.note} - (RTP Normal: {preset.rtpNormal}%, Buy
											Feature: {preset.rtpBuyFeature}%)
										</SelectItem>
									),
								)}
							</SelectContent>
						</Select>
						<FormDescription>
							เลือก RTP Preset สำหรับเกมที่รองรับ
						</FormDescription>
						<FormMessage />
					</FormItem>
				)}
			/>

			{/* Conditional fields based on game type */}
			{isSlot ? (
				<div className="space-y-4 rounded-md bg-muted/50 p-4">
					<div className="text-sm font-medium">
						ตั้งค่าสำหรับเกม Slot (เพื่อแสดงเท่านั้น ไม่ใช้สำหรับคำนวณจริง)
					</div>

					<FormField
						control={form.control}
						name={`games.${index}.slotData.winProbability`}
						render={({ field }) => (
							<FormItem>
								<FormLabel>Win Probability (%)</FormLabel>
								<FormControl>
									<Input
										{...field}
										type="number"
										min={0}
										max={100}
										onChange={(e) => field.onChange(Number(e.target.value))}
										disabled={disabled}
									/>
								</FormControl>
								<FormDescription>โอกาสชนะ (0-100%)</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name={`games.${index}.slotData.freeSpinChance`}
						render={({ field }) => (
							<FormItem>
								<FormLabel>Free Spin Chance (%)</FormLabel>
								<FormControl>
									<Input
										{...field}
										type="number"
										min={0}
										max={100}
										onChange={(e) => field.onChange(Number(e.target.value))}
										disabled={disabled}
									/>
								</FormControl>
								<FormDescription>โอกาสได้ฟรีสปิน (0-100%)</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name={`games.${index}.slotData.maxMultiplier`}
						render={({ field }) => (
							<FormItem>
								<FormLabel>Max Multiplier</FormLabel>
								<FormControl>
									<Input
										{...field}
										type="number"
										min={1}
										onChange={(e) => field.onChange(Number(e.target.value))}
										disabled={disabled}
									/>
								</FormControl>
								<FormDescription>ตัวคูณสูงสุด (เช่น 13000)</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>
			) : (
				<div className="space-y-4 rounded-md bg-muted/50 p-4">
					<div className="text-sm font-medium">
						ตั้งค่าสำหรับเกมทั่วไป (เพื่อแสดงเท่านั้น ไม่ใช้สำหรับคำนวณจริง)
					</div>

					<FormField
						control={form.control}
						name={`games.${index}.nonSlotData.bonusAmount`}
						render={({ field }) => (
							<FormItem>
								<FormLabel>Bonus Amount</FormLabel>
								<FormControl>
									<Input
										{...field}
										type="number"
										min={0}
										value={field.value ?? ''}
										onChange={(e) =>
											field.onChange(
												e.target.value === '' ? 0 : Number(e.target.value),
											)
										}
										disabled={disabled}
									/>
								</FormControl>
								<FormDescription>จำนวนโบนัส (บาท)</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>
			)}
		</>
	)
}

interface RandomizedModeFieldsProps {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	form: UseFormReturn<any>
	index: number
	disabled: boolean
	selectedGameTypes: GameType[]
	hasSlotType: boolean
	hasNonSlotType: boolean
	onGameTypeToggle: (type: GameType) => void
	providerFilterItems: { id: string; name: string; displayName?: string }[]
	gameFilterItems: { id: string; name: string; displayName?: string }[]
	presetFilterItems: { id: string; name: string; displayName?: string }[]
	providerFilter: {
		includedProviderIds: string[]
		excludedProviderIds: string[]
	}
	gameFilter: { includedGameIds: string[]; excludedGameIds: string[] }
	presetFilter: { includedPresets: string[]; excludedPresets: string[] }
}

const RandomizedModeFields = ({
	form,
	index,
	disabled,
	selectedGameTypes,
	hasSlotType,
	hasNonSlotType,
	onGameTypeToggle,
	providerFilterItems,
	gameFilterItems,
	presetFilterItems,
	providerFilter,
	gameFilter,
	presetFilter,
}: RandomizedModeFieldsProps) => {
	return (
		<>
			{/* Game Types Checkboxes */}
			<div className="space-y-3">
				<Label className="text-sm font-medium">
					ประเภทเกม (เลือกได้หลายรายการ)
				</Label>
				<div className="flex flex-wrap gap-4">
					{GAME_TYPES.map((type) => (
						<div key={type} className="flex items-center space-x-2">
							<Checkbox
								id={`game-type-${index}-${type}`}
								checked={selectedGameTypes.includes(type)}
								onCheckedChange={() => onGameTypeToggle(type)}
								disabled={disabled}
							/>
							<Label
								htmlFor={`game-type-${index}-${type}`}
								className="text-sm font-normal"
							>
								{type}
							</Label>
						</div>
					))}
				</div>
				{selectedGameTypes.length === 0 && (
					<p className="text-xs text-destructive">
						กรุณาเลือกประเภทเกมอย่างน้อย 1 ประเภท
					</p>
				)}
				<FormMessage />
			</div>

			{/* Filters Section */}
			<div className="space-y-4">
				<Label className="text-sm font-medium">ตัวกรอง (ไม่บังคับ)</Label>

				{/* Provider Filter */}
				<FilterSelector
					label="ผู้ให้บริการเกม"
					items={providerFilterItems}
					includedIds={providerFilter.includedProviderIds || []}
					excludedIds={providerFilter.excludedProviderIds || []}
					onIncludedChange={(ids) =>
						form.setValue(
							`games.${index}.providerFilter.includedProviderIds`,
							ids,
						)
					}
					onExcludedChange={(ids) =>
						form.setValue(
							`games.${index}.providerFilter.excludedProviderIds`,
							ids,
						)
					}
					placeholder="เลือกผู้ให้บริการ"
					searchPlaceholder="ค้นหาผู้ให้บริการ..."
					disabled={disabled}
				/>

				{/* Game Filter */}
				<FilterSelector
					label="เกม"
					items={gameFilterItems}
					includedIds={gameFilter.includedGameIds || []}
					excludedIds={gameFilter.excludedGameIds || []}
					onIncludedChange={(ids) =>
						form.setValue(`games.${index}.gameFilter.includedGameIds`, ids)
					}
					onExcludedChange={(ids) =>
						form.setValue(`games.${index}.gameFilter.excludedGameIds`, ids)
					}
					placeholder="เลือกเกม"
					searchPlaceholder="ค้นหาเกม..."
					disabled={disabled}
				/>

				{/* Preset Filter */}
				<FilterSelector
					label="RTP Preset"
					items={presetFilterItems}
					includedIds={presetFilter.includedPresets || []}
					excludedIds={presetFilter.excludedPresets || []}
					onIncludedChange={(ids) =>
						form.setValue(`games.${index}.presetFilter.includedPresets`, ids)
					}
					onExcludedChange={(ids) =>
						form.setValue(`games.${index}.presetFilter.excludedPresets`, ids)
					}
					placeholder="เลือก RTP Preset"
					searchPlaceholder="ค้นหา RTP Preset..."
					disabled={disabled}
				/>
			</div>

			{/* Slot Data Range - shown if SLOT is selected */}
			{hasSlotType && (
				<div className="space-y-4 rounded-md bg-blue-50 p-4 dark:bg-blue-950/30">
					<div className="text-sm font-medium">
						ช่วงค่าสำหรับเกม Slot (สุ่มค่าระหว่าง Min-Max)
					</div>

					{/* Win Probability Range */}
					<div className="space-y-2">
						<Label className="text-sm">Win Probability (%)</Label>
						<div className="flex items-center gap-2">
							<FormField
								control={form.control}
								name={`games.${index}.slotDataRange.winProbabilityMin`}
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormControl>
											<Input
												{...field}
												type="number"
												min={0}
												max={100}
												placeholder="Min"
												onChange={(e) => field.onChange(Number(e.target.value))}
												disabled={disabled}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<span className="text-muted-foreground">-</span>
							<FormField
								control={form.control}
								name={`games.${index}.slotDataRange.winProbabilityMax`}
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormControl>
											<Input
												{...field}
												type="number"
												min={0}
												max={100}
												placeholder="Max"
												onChange={(e) => field.onChange(Number(e.target.value))}
												disabled={disabled}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<span className="text-sm text-muted-foreground">%</span>
						</div>
					</div>

					{/* Free Spin Chance Range */}
					<div className="space-y-2">
						<Label className="text-sm">Free Spin Chance (%)</Label>
						<div className="flex items-center gap-2">
							<FormField
								control={form.control}
								name={`games.${index}.slotDataRange.freeSpinChanceMin`}
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormControl>
											<Input
												{...field}
												type="number"
												min={0}
												max={100}
												placeholder="Min"
												onChange={(e) => field.onChange(Number(e.target.value))}
												disabled={disabled}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<span className="text-muted-foreground">-</span>
							<FormField
								control={form.control}
								name={`games.${index}.slotDataRange.freeSpinChanceMax`}
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormControl>
											<Input
												{...field}
												type="number"
												min={0}
												max={100}
												placeholder="Max"
												onChange={(e) => field.onChange(Number(e.target.value))}
												disabled={disabled}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<span className="text-sm text-muted-foreground">%</span>
						</div>
					</div>

					{/* Max Multiplier Range */}
					<div className="space-y-2">
						<Label className="text-sm">Max Multiplier</Label>
						<div className="flex items-center gap-2">
							<FormField
								control={form.control}
								name={`games.${index}.slotDataRange.maxMultiplierMin`}
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormControl>
											<Input
												{...field}
												type="number"
												min={1}
												placeholder="Min"
												onChange={(e) => field.onChange(Number(e.target.value))}
												disabled={disabled}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<span className="text-muted-foreground">-</span>
							<FormField
								control={form.control}
								name={`games.${index}.slotDataRange.maxMultiplierMax`}
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormControl>
											<Input
												{...field}
												type="number"
												min={1}
												placeholder="Max"
												onChange={(e) => field.onChange(Number(e.target.value))}
												disabled={disabled}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<span className="text-sm text-muted-foreground">x</span>
						</div>
					</div>
				</div>
			)}

			{/* Non-Slot Data Range - shown if any non-SLOT type is selected */}
			{hasNonSlotType && (
				<div className="space-y-4 rounded-md bg-green-50 p-4 dark:bg-green-950/30">
					<div className="text-sm font-medium">
						ช่วงค่าสำหรับเกมทั่วไป (สุ่มค่าระหว่าง Min-Max)
					</div>

					{/* Bonus Amount Range */}
					<div className="space-y-2">
						<Label className="text-sm">Bonus Amount</Label>
						<div className="flex items-center gap-2">
							<FormField
								control={form.control}
								name={`games.${index}.nonSlotDataRange.bonusAmountMin`}
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormControl>
											<Input
												{...field}
												type="number"
												min={0}
												placeholder="Min"
												onChange={(e) => field.onChange(Number(e.target.value))}
												disabled={disabled}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<span className="text-muted-foreground">-</span>
							<FormField
								control={form.control}
								name={`games.${index}.nonSlotDataRange.bonusAmountMax`}
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormControl>
											<Input
												{...field}
												type="number"
												min={0}
												placeholder="Max"
												onChange={(e) => field.onChange(Number(e.target.value))}
												disabled={disabled}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<span className="text-sm text-muted-foreground">บาท</span>
						</div>
					</div>
				</div>
			)}
		</>
	)
}
